import {measureChip, drawTaskSetChip} from './taskSetChipPainter';
import {mayMarkTaskSetsOnSurface, TaskSetSurfacePageComponent} from './taskSetPsGate';

/**
 * Whether the SIDEBAR row carries a set marker, and what it costs the name.
 *
 * Split out of task_group so it can be tested: that file draws into the group
 * section canvas and imports most of the timeline to do it, which cannot be loaded
 * in a jest unit test. What is left behind in task_group is the drawing call.
 *
 * The sidebar marker is identity only -- the set's colour and the repeat icon, no
 * "n of y". The bar chip keeps the position and the click. The split is the point:
 * a bar's width varies with duration and zoom, so the bar marker degrades to an
 * icon and then to a 4px edge exactly when a run of identically-named instances is
 * hardest to tell apart. The sidebar row is a fixed width and always drawn, and it
 * also survives the weekend-collapsed axis, where an instance dated on a weekend
 * draws in the wrong week or not at all.
 */

export interface SidebarTaskSetChip {
	/** False when the row shows no marker; `width` is then 0. */
	show: boolean;
	/** The pill itself. */
	chipWidth: number;
	/**
	 * What the name must give up: the pill plus the gap before it. Subtract this
	 * from the name's budget BEFORE trimming, or the marker lands on top of the
	 * text -- sidebar names are already truncated.
	 */
	reservedWidth: number;
}

export interface SidebarTaskSetTask {
	taskSetId?: string | null;
	/**
	 * False where the viewer has partial access to the task's project. The marker is
	 * withheld then, exactly as the bar chip is (task_item.js) and as the server
	 * already withholds the data: TaskType's `taskSet` and `taskSetSequence`
	 * resolvers return null without it, because "the rule describes work across the
	 * whole project, so it is not for someone with partial access".
	 *
	 * The check lives here rather than at the call site so a second caller cannot
	 * quietly omit it -- a program or connected-group timeline is one surface
	 * carrying several projects, so the leak would be a marker beside a task whose
	 * bar is deliberately left plain.
	 */
	fullAccessToProject?: boolean;
}

export const measureSidebarTaskSetChip = ({
	canvasContext,
	task,
	pageComponent,
	flagEnabled,
	gap,
}: {
	canvasContext: CanvasRenderingContext2D;
	task?: SidebarTaskSetTask | null;
	pageComponent?: TaskSetSurfacePageComponent | null;
	/** `hasFeatureFlag('recurring_tasks_set')`, passed in so this stays pure. */
	flagEnabled: boolean;
	/** Space between the task name and the pill. */
	gap: number;
}): SidebarTaskSetChip => {
	if (!task?.taskSetId || !task.fullAccessToProject || !flagEnabled || !mayMarkTaskSetsOnSurface(pageComponent)) {
		return {show: false, chipWidth: 0, reservedWidth: 0};
	}

	// label null forces the icon-only tier, and an unbounded width keeps the tier
	// decision here from depending on the row's own arithmetic -- the sidebar has
	// room for the pill by construction, unlike a bar.
	const {width} = measureChip({canvasContext, availableWidth: Infinity, edgeWidthAvailable: 0, label: null});

	return {show: true, chipWidth: width, reservedWidth: width + gap};
};

/**
 * Draws the marker after the row's trimmed name, or nothing when there is none.
 *
 * Placed after the name rather than at a fixed x so it travels with its own row's
 * text instead of floating in the gutter. A no-op when `chip.show` is false, so the
 * caller carries no branch -- which keeps task_group, the one file on this path
 * that cannot be loaded in jest, down to a single delegating call.
 */
export const drawSidebarTaskSetChip = ({
	canvasContext,
	chip,
	taskSetId,
	nameX,
	nameWidth,
	gap,
	y,
	rowHeight,
	colourFor,
}: {
	canvasContext: CanvasRenderingContext2D;
	chip: SidebarTaskSetChip;
	taskSetId?: string | null;
	/** Left edge of the row's name text. */
	nameX: number;
	/** Width of the TRIMMED name, measured under its own font by the caller. */
	nameWidth: number;
	gap: number;
	y: number;
	rowHeight: number;
	/** `taskSetColour`, injected so this module stays free of the scoping-page tree. */
	colourFor: (id: string) => {background: string; foreground: string};
}): void => {
	if (!chip.show || !taskSetId) {
		return;
	}

	drawTaskSetChip({
		canvasContext,
		x: nameX + nameWidth + gap,
		y,
		barHeight: rowHeight,
		tier: 'icon',
		width: chip.chipWidth,
		label: null,
		colour: colourFor(taskSetId),
	});
};
